import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { formatDate } from '../../../../../../utils/date-format';
import {
  InstallmentWithPlanItemsDetails,
  PaymentInstallmentEntity,
  PaymentInstallmentStatus,
} from '../../../../../../api/types/payments/installments/entities';
import { deleteInstallmentPlanOperation } from '../operations/delete-installment-plan-operation';
import { cancelInstallmentItemOperation } from '../operations/cancel-installment-item-operation';
import { UserSummaryType } from '../../../../../../api/types/user/user-summary.type';
import { WarningDialog } from '../../../../../../component/ui/warning-dialog/warning-dialog';
import { CancelInstallmentWarningDialog } from './cancel-installment-warning-dialog/cancel-installment-warning-dialog';
import { ReduceInstallmentPlanTotalPriceDialog } from './reduce-installment-plan-total-price-dialog/reduce-installment-plan-total-price-dialog';
import { ReduceInstallmentPaymentDialog } from './reduce-installment-payment-dialog/reduce-installment-payment-dialog';

interface InstallmentsTableProps {
  user: UserSummaryType;
  plan: InstallmentWithPlanItemsDetails;
  onDeleted?: () => void;
}

const getStatusSeverity = (
  status: PaymentInstallmentStatus,
): 'success' | 'warning' | 'danger' | 'info' => {
  switch (status) {
    case PaymentInstallmentStatus.PAID:
      return 'success';
    case PaymentInstallmentStatus.PENDING:
      return 'warning';
    case PaymentInstallmentStatus.FAILED:
    case PaymentInstallmentStatus.CANCELLED:
      return 'danger';
    default:
      return 'warning';
  }
};

const getStatusLabel = (status: PaymentInstallmentStatus): string => {
  switch (status) {
    case PaymentInstallmentStatus.PENDING:
      return 'ממתין לתשלום';
    case PaymentInstallmentStatus.PAID:
      return 'שולם';
    case PaymentInstallmentStatus.FAILED:
      return 'נכשל';
    case PaymentInstallmentStatus.CANCELLED:
      return 'בוטל';
    default:
      return 'לא ידוע';
  }
};

export const InstallmentsTable: React.FC<InstallmentsTableProps> = ({
  user,
  plan,
  onDeleted,
}) => {
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showReduceTotalPriceDialog, setShowReduceTotalPriceDialog] =
    useState(false);

  // Cancel installment state
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [installmentToCancel, setInstallmentToCancel] =
    useState<PaymentInstallmentEntity | null>(null);

  // Reduce installment payment state
  const [showReducePaymentDialog, setShowReducePaymentDialog] = useState(false);
  const [installmentToReduce, setInstallmentToReduce] =
    useState<PaymentInstallmentEntity | null>(null);

  const handleDeleteClick = () => {
    setShowDeleteWarning(true);
  };

  const handleReduceTotalPriceClick = () => {
    setShowReduceTotalPriceDialog(true);
  };

  const handleDeleteConfirm = async () => {
    await deleteInstallmentPlanOperation(
      user,
      plan.installmentPlan.id,
      setDeleteLoading,
      () => {
        setShowDeleteWarning(false);
        onDeleted?.();
      },
    );
  };

  const handleCancelInstallmentClick = (
    installment: PaymentInstallmentEntity,
  ) => {
    setInstallmentToCancel(installment);
    setShowCancelDialog(true);
  };

  const handleReducePaymentClick = (installment: PaymentInstallmentEntity) => {
    setInstallmentToReduce(installment);
    setShowReducePaymentDialog(true);
  };

  const handleCancelInstallmentConfirm = async (
    shouldSplitRemainder: boolean,
  ) => {
    if (!installmentToCancel) return;

    await cancelInstallmentItemOperation(
      user,
      plan.installmentPlan.id,
      installmentToCancel.id,
      shouldSplitRemainder,
      setCancelLoading,
      () => {
        setShowCancelDialog(false);
        setInstallmentToCancel(null);
      },
    );
  };

  const canCancelInstallment = (status: PaymentInstallmentStatus): boolean => {
    return (
      status === PaymentInstallmentStatus.PENDING ||
      status === PaymentInstallmentStatus.FAILED
    );
  };

  const canReduceInstallmentPayment = (
    status: PaymentInstallmentStatus,
  ): boolean => {
    return (
      status === PaymentInstallmentStatus.PENDING ||
      status === PaymentInstallmentStatus.FAILED
    );
  };

  return (
    <div>
      <WarningDialog
        header="מחיקת הוראת קבע"
        isVisible={showDeleteWarning}
        onHide={() => setShowDeleteWarning(false)}
        addIcon
        content="האם אתה בטוח שברצונך למחוק את הוראת הקבע?"
        dangerousContent="פעולה זו תבטל את כל התשלומים הממתינים ואינה ניתנת לביטול."
        onConfirm={handleDeleteConfirm}
        confirmLabel="מחק"
        confirmLoading={deleteLoading}
        cancelLabel="ביטול"
      />

      {/* Cancel Installment Dialog */}
      <CancelInstallmentWarningDialog
        visible={showCancelDialog}
        onHide={() => {
          setShowCancelDialog(false);
          setInstallmentToCancel(null);
        }}
        installment={installmentToCancel}
        onConfirm={handleCancelInstallmentConfirm}
        loading={cancelLoading}
      />

      <ReduceInstallmentPaymentDialog
        visible={showReducePaymentDialog}
        user={user}
        installmentPlanId={plan.installmentPlan.id}
        installment={installmentToReduce}
        onHide={() => {
          setShowReducePaymentDialog(false);
          setInstallmentToReduce(null);
        }}
      />

      <ReduceInstallmentPlanTotalPriceDialog
        visible={showReduceTotalPriceDialog}
        user={user}
        installmentPlanId={plan.installmentPlan.id}
        onHide={() => setShowReduceTotalPriceDialog(false)}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '12px',
          gap: '8px',
        }}
      >
        <Button
          icon="pi pi-minus-circle"
          label="הפחת סכום"
          severity="warning"
          size="small"
          onClick={handleReduceTotalPriceClick}
        />
        <Button
          icon="pi pi-trash"
          label="מחק הוראת קבע"
          severity="danger"
          size="small"
          onClick={handleDeleteClick}
        />
      </div>
      <DataTable
        value={plan.installment}
        rowHover
        emptyMessage={
          <div style={{ textAlign: 'right' }}>לא נמצאו תשלומים</div>
        }
        dataKey="id"
        scrollable
        scrollHeight="300px"
        tableStyle={{
          direction: 'rtl',
        }}
      >
        <Column
          field="installmentNumber"
          header="תשלום מספר"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(rowData) =>
            `${rowData.installmentNumber} / ${plan.installmentPlan.numberOfInstallments}`
          }
        />
        <Column
          field="amount"
          header="סכום תשלום"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(rowData) => `₪${rowData.amount?.toLocaleString()}`}
        />
        <Column
          field="dueDate"
          header="תאריך לתשלום"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(rowData) => formatDate(rowData.dueDate)}
        />
        <Column
          field="paidAt"
          header="תאריך תשלום בפועל"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(rowData) =>
            rowData.paidAt ? formatDate(rowData.paidAt) : '-'
          }
        />
        <Column
          field="status"
          header="סטטוס"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(rowData) => (
            <Tag
              style={{ minWidth: '100px' }}
              value={getStatusLabel(rowData.status)}
              severity={getStatusSeverity(rowData.status)}
            />
          )}
        />
        <Column
          header="פעולות"
          style={{ textAlign: 'center', width: '100px' }}
          headerStyle={{ textAlign: 'center' }}
          body={(rowData: PaymentInstallmentEntity) => (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                {canReduceInstallmentPayment(rowData.status) ? (
                  <Button
                    icon="pi pi-minus"
                    severity="warning"
                    size="small"
                    rounded
                    text
                    tooltip="הפחת תשלום"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleReducePaymentClick(rowData)}
                  />
                ) : (
                  <span style={{ color: '#9CA3AF' }}>-</span>
                )}

                {canCancelInstallment(rowData.status) ? (
                  <Button
                    icon="pi pi-ban"
                    severity="danger"
                    size="small"
                    rounded
                    text
                    tooltip="בטל תשלום"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleCancelInstallmentClick(rowData)}
                  />
                ) : (
                  <span style={{ color: '#9CA3AF' }}>-</span>
                )}
              </div>
            </div>
          )}
        />
      </DataTable>
    </div>
  );
};
